import React, { useEffect, useRef, useState, useCallback } from "react";
import { X, ScanLine, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, describeError } from "@/lib/api";

const fileToB64 = (file) =>
  new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result).split(",")[1]);
    r.onerror = rej;
    r.readAsDataURL(file);
  });

const Line = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5">
    <span className="text-[11px] font-semibold uppercase tracking-[.04em] text-[var(--gray2)]">{label}</span>
    <span className="text-[12.5px] font-bold text-[var(--ink)]">{value || "—"}</span>
  </div>
);

// Insurance · Visit — scan an insurance card or link a payer + member id manually.
export default function InsuranceModal({ note, initialScan = false, onClose, onUpdated }) {
  const [payers, setPayers] = useState([]);
  const [payerId, setPayerId] = useState("");
  const [memberId, setMemberId] = useState("");
  const [busy, setBusy] = useState(""); // "scan" | "link"
  const [err, setErr] = useState("");
  const [scan, setScan] = useState(null);
  const fileRef = useRef(null);

  const ins = note?.insurance || {};
  const appliedPayer = ins.payer_name || note?.payer_name || null;
  const appliedMember = ins.member_id || note?.member_id || null;

  useEffect(() => {
    const cid = note?.client_id;
    api.get("/api/edi/note-to-claim/payers", { params: cid ? { client_id: cid } : {} })
      .then((r) => setPayers(r.data?.payers || []))
      .catch(() => setPayers([]));
  }, [note?.client_id]);

  useEffect(() => { if (initialScan) fileRef.current?.click(); }, [initialScan]);

  const onScanFile = useCallback(async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy("scan"); setErr(""); setScan(null);
    try {
      const b64 = await fileToB64(f);
      const { data } = await api.post(`/api/edi/note-to-claim/notes/${note.id}/scan-insurance-card`, { file_base64: b64 });
      const resolved = data?.insurance || data || {};
      setScan(resolved);
      if (resolved.payer_id) setPayerId(resolved.payer_id);
      if (resolved.member_id) setMemberId(resolved.member_id);
      toast.success("Insurance card scanned");
      onUpdated?.();
    } catch (ex) { setErr(describeError(ex)); } finally { setBusy(""); }
  }, [note, onUpdated]);

  const onLink = useCallback(async () => {
    if (!payerId) { setErr("Choose an approved payer."); return; }
    setBusy("link"); setErr("");
    try {
      const p = payers.find((x) => String(x.id) === String(payerId));
      await api.patch(`/api/edi/note-to-claim/notes/${note.id}/insurance`, {
        payer_id: payerId, payer_name: p?.name, member_id: memberId || undefined,
      });
      toast.success("Insurance linked to the visit");
      onUpdated?.();
      onClose?.();
    } catch (ex) { setErr(describeError(ex)); } finally { setBusy(""); }
  }, [payerId, memberId, payers, note, onUpdated, onClose]);

  if (!note) return null;

  return (
    <div data-testid="insurance-modal"
      style={{ position: "fixed", inset: 0, zIndex: 9000, background: "rgba(20,20,18,.5)", backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{ background: "#F6F6F4", borderRadius: 18, width: "min(460px, 96vw)", maxHeight: "92vh", overflowY: "auto", boxShadow: "0 30px 80px rgba(20,20,18,.35)" }}>
        <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-black/[.07]">
          <div className="flex items-center gap-2.5">
            <div className="w-[30px] h-[30px] rounded-[9px] flex items-center justify-center text-white" style={{ background: "linear-gradient(145deg,#1a1a1a,#000)" }}>
              <Link2 size={15} />
            </div>
            <div>
              <div className="text-[15px] font-bold text-[var(--ink)] tracking-[-.2px]">Insurance · Visit</div>
              <div className="text-[11px] text-[var(--gray2)]">{note.visit_id || note.id}</div>
            </div>
          </div>
          <button onClick={onClose} data-testid="insurance-close" className="text-[var(--gray2)] hover:text-[var(--ink)] p-1"><X size={17} /></button>
        </div>

        <div className="px-6 py-5 flex flex-col gap-4">
          <div className="rounded-[12px] bg-white border border-black/[.06] p-3.5">
            <div className="text-[11px] font-bold uppercase tracking-[.05em] text-[#9A5A00] mb-1">Applied to claim</div>
            <Line label="Payer" value={appliedPayer} />
            <Line label="Member ID" value={appliedMember} />
          </div>

          {/* scan */}
          <div className="rounded-[12px] bg-white border border-black/[.06] p-3.5">
            <div className="flex items-center justify-between">
              <div className="text-[12.5px] font-bold text-[var(--ink)] flex items-center gap-1.5"><ScanLine size={14} /> Insurance card</div>
              <button onClick={() => fileRef.current?.click()} disabled={busy === "scan"} data-testid="insurance-scan-btn"
                className="inline-flex items-center gap-1.5 text-[11.5px] font-bold rounded-full px-3 py-1.5 transition"
                style={{ background: "#FBF0DF", color: "#9A5A00", opacity: busy === "scan" ? 0.6 : 1 }}>
                {busy === "scan" ? <Loader2 size={12} className="animate-spin" /> : <ScanLine size={12} />} Add &amp; scan
              </button>
            </div>
            <p className="text-[11.5px] text-[var(--gray2)] mt-1.5">
              {scan ? `Scanned: ${scan.payer_name || "payer resolved"}${scan.member_id ? ` · ${scan.member_id}` : ""}${scan.confidence ? ` · ${scan.confidence} confidence` : ""}` : "No insurance card on this visit. Add one to verify coverage."}
            </p>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/jpg,application/pdf" className="hidden" onChange={onScanFile} data-testid="insurance-file-input" />
          </div>

          {/* manual link */}
          <div className="rounded-[12px] bg-white border border-black/[.06] p-3.5 flex flex-col gap-2.5">
            <div className="text-[12.5px] font-bold text-[var(--ink)] flex items-center gap-1.5"><Link2 size={14} /> Link insurance manually</div>
            <div>
              <label className="text-[11px] font-semibold text-[var(--gray2)]">Payer (name + ID)</label>
              <select value={payerId} onChange={(e) => setPayerId(e.target.value)} data-testid="insurance-payer-select"
                className="w-full mt-1 px-3 py-2.5 rounded-[10px] border border-black/[.12] text-[13px] bg-white outline-none focus:border-black/[.3]">
                <option value="">Select approved payer…</option>
                {payers.map((p) => <option key={p.id} value={p.id}>{p.name} · {p.id}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[11px] font-semibold text-[var(--gray2)]">Member ID</label>
              <input value={memberId} onChange={(e) => setMemberId(e.target.value)} placeholder="Enter member / subscriber ID" data-testid="insurance-member-input"
                className="w-full mt-1 px-3 py-2.5 rounded-[10px] border border-black/[.12] text-[13px] bg-white outline-none focus:border-black/[.3]" />
            </div>
            {err ? <div className="text-[12px] font-semibold text-[#C8372D]" data-testid="insurance-error">{err}</div> : null}
            <button onClick={onLink} disabled={busy === "link" || !payerId} data-testid="insurance-link-btn"
              className="w-full inline-flex items-center justify-center gap-1.5 text-[13px] font-bold rounded-[11px] py-2.5 transition"
              style={{ background: "#141412", color: "#fff", opacity: (busy === "link" || !payerId) ? 0.5 : 1 }}>
              {busy === "link" ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />} Link insurance
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
